import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Search } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogCard = ({ post, index }) => {
  const { language } = useLanguage();
  const title = language === 'it' ? post.title_it : post.title_en;
  const excerpt = language === 'it' ? post.excerpt_it : post.excerpt_en;

  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      data-testid={`blog-card-${post.slug}`}
    >
      <Link to={`/blog/${post.slug}`} className="group block">
        <div className="relative aspect-[16/10] overflow-hidden rounded-2xl mb-4">
          <img
            src={post.image_url}
            alt={title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        
        <span className="text-xs font-medium uppercase tracking-wider text-[#C9B896]">
          {post.category}
        </span>
        
        <h3 className="text-lg font-semibold text-white group-hover:text-[#C9B896] transition-colors mt-2 mb-2">
          {title}
        </h3>
        
        <p className="text-[#888] text-sm line-clamp-2">
          {excerpt}
        </p>
      </Link>
    </motion.article>
  );
};

const BlogSection = ({ showAll = false, showSearch = false }) => {
  const { language } = useLanguage();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  const texts = {
    it: {
      title: "Dal Blog",
      subtitle: "Consigli, storie e ispirazioni per il tuo prossimo viaggio",
      viewAll: "Tutti gli articoli",
      search: "Cerca articoli...",
      all: "Tutti"
    },
    en: {
      title: "From the Blog",
      subtitle: "Tips, stories and inspiration for your next trip",
      viewAll: "All articles",
      search: "Search articles...",
      all: "All"
    }
  };

  const t = texts[language];

  const categoryLabels = {
    tips: language === 'it' ? 'Consigli' : 'Tips',
    destinations: language === 'it' ? 'Destinazioni' : 'Destinations',
    food: language === 'it' ? 'Cibo' : 'Food'
  };

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const params = {};
        if (selectedCategory) params.category = selectedCategory;
        if (searchQuery) params.search = searchQuery;
        if (!showAll) params.limit = 3;
        
        const response = await axios.get(`${API}/blog`, { params });
        setPosts(response.data);
      } catch (error) {
        console.error('Error fetching blog posts:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, [showAll, selectedCategory, searchQuery]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/blog/categories`);
        setCategories(response.data.categories || []);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    if (showSearch) fetchCategories();
  }, [showSearch]);

  // Don't show loading state or empty section on homepage
  if (loading && !showAll) {
    return null;
  }

  // If no posts and not on dedicated blog page, hide section
  if (posts.length === 0 && !showAll) {
    return null;
  }

  if (loading) {
    return (
      <section className="py-24 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="space-y-4 animate-pulse">
                <div className="aspect-[16/10] bg-[#1a1a1a] rounded-2xl" />
                <div className="h-4 bg-[#1a1a1a] rounded w-1/4" />
                <div className="h-6 bg-[#1a1a1a] rounded w-3/4" />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-24 bg-[#0A0A0A]" data-testid="blog-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
        {/* Header */}
        {!showAll && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {t.title}
            </h2>
            <p className="text-lg text-[#888]">
              {t.subtitle}
            </p>
          </motion.div>
        )}

        {/* Search and Filters for blog page */}
        {showSearch && (
          <div className="mb-12 space-y-6">
            <div className="max-w-md mx-auto relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#666]" />
              <input
                type="text"
                placeholder={t.search}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-12 pl-12 pr-6 rounded-full bg-[#1a1a1a] border border-[#333] text-white placeholder:text-[#666] focus:outline-none focus:border-[#C9B896]"
                data-testid="blog-search"
              />
            </div>

            <div className="flex flex-wrap justify-center gap-3">
              <button
                onClick={() => setSelectedCategory('')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === '' 
                    ? 'bg-[#C9B896] text-black' 
                    : 'bg-[#1a1a1a] text-[#888] hover:text-white'
                }`}
              >
                {t.all}
              </button>
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    selectedCategory === cat 
                      ? 'bg-[#C9B896] text-black' 
                      : 'bg-[#1a1a1a] text-[#888] hover:text-white'
                  }`}
                >
                  {categoryLabels[cat] || cat}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Posts Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {posts.map((post, index) => (
            <BlogCard key={post._id || post.slug} post={post} index={index} />
          ))}
        </div>

        {/* View All Link */}
        {!showAll && posts.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-center mt-12"
          >
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-[#C9B896] hover:text-white font-medium transition-colors group"
              data-testid="view-all-blog"
            >
              {t.viewAll}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        )}

        {/* Empty state */}
        {posts.length === 0 && !loading && (
          <p className="text-center text-[#888]">
            {language === 'it' ? 'Nessun articolo trovato' : 'No articles found'}
          </p>
        )}
      </div>
    </section>
  );
};

export default BlogSection;
